import className from "classnames/bind";
import { useTranslation } from "next-i18next";

import amazonStyles from "./amazon-uk-special-content.module.scss";
import styles from "./retailerPage.module.scss";

type Props = {
    showDisclaimer?: boolean;
};

export const AmazonUKSpecialContent = ({ showDisclaimer }: Props) => {
    const { t } = useTranslation();
    const TextDetail = () => {
        const textDetaileSectionClassName = className({
            [amazonStyles.amazon]: true,
        });

        return (
            <div className={styles.retailerTextDetail}>
                <div className={textDetaileSectionClassName}>
                    <h2>How can I use Amazon with Clearpay</h2>
                    <p>
                        You can shop at Amazon and pay with Clearpay using a single-use payment
                        card.
                    </p>
                    <p>
                        <b> We recommend using the Amazon app</b> for the best experience.
                    </p>
                    <p>Two ways to shop:</p>
                    <ul>
                        <li>
                            Amazon app (recommended) - Open the Clearpay app, search for Amazon in
                            the Shop directory, and you'll be directed to the Amazon app
                        </li>
                        <li>
                            Amazon website - Shop on Amazon's website and use the Clearpay app to
                            create a single-use card for payment
                        </li>
                    </ul>

                    <h3>How to shop at Amazon with Clearpay</h3>
                    <p>
                        <b>Step 1: Add items to your Amazon cart</b>
                    </p>
                    <ul>
                        <li>
                            Amazon website - Shop on Amazon's website and use the Clearpay app to
                            create a single-use card for payment
                        </li>
                    </ul>
                    <p>
                        <b>Step 2: Create your single-use card in the Clearpay App</b>
                    </p>
                    <ol>
                        <li>Open the Clearpay app</li>
                        <li>
                            Search for Amazon in the Shop directory (or if you're already shopping,
                            you'll see the payment screen)
                        </li>
                        <li>
                            Enter your total purchase amount
                            <ul>
                                <li>Important: Include shipping costs in this amount</li>
                            </ul>
                        </li>

                        <li>Review your payment schedule</li>
                        <li>Tap to create your single-use card</li>
                    </ol>
                    <p>
                        <b>
                            Step 3: When the single-use card is created, you’ll need to add a new
                            payment method at Amazon checkout and enter the card details - manually
                            following the steps below:
                        </b>
                    </p>
                    <ol>
                        <li>
                            In the Clearpay app, you'll see:
                            <ul>
                                <li>Card number</li>
                                <li>Expiration date</li>
                                <li>CVV</li>
                                <li>Name on card</li>
                            </ul>
                        </li>
                        <li>
                            Copy each detail from Clearpay and paste it into Amazon
                            <ul>
                                <li>Tap "Copy" next to each field in Clearpay</li>
                                <li>Switch to Amazon and paste into the corresponding field</li>
                                <li>Repeat for each field</li>
                            </ul>
                        </li>
                        <li>
                            Before you leave the card screen, make sure you've copied all details -
                            they'll be hidden once you navigate away
                        </li>
                        <li>Complete your Amazon purchase</li>
                    </ol>
                    <p>
                        <b>Step 4: Clean up (recommended)</b>
                    </p>
                    <ul>
                        <li>
                            After your order is complete, remove the single-use card from your saved
                            payment methods in Amazon. You'll create a fresh card for each new
                            order.
                        </li>
                    </ul>

                    <h3>TROUBLESHOOTING</h3>
                    <h4>I lost the card details before finishing</h4>
                    <p>
                        Don’t worry! You can create a new card. When you enter the new card in
                        Amazon, you may see multiple saved cards from previous single-use payment
                        transactions.Check that the last 4 digits match your newly created
                        single-use card.
                    </p>
                    <h4>Amazon says my order was declined</h4>
                    <p>
                        This usually means your order total is higher than the single-use card
                        amount..
                    </p>
                    <p>
                        To complete the order you will need to generate a new single use card where
                        the amount is enough to cover the Amazon checkout total (including shipping)
                    </p>
                    <p>
                        <b>Note: If you don't cancel the declined order,</b> Amazon may attempt to
                        take a payment from a different payment method.
                    </p>
                    <h4>My order isn't showing in Clearpay</h4>
                    <p>
                        Orders will reflect in your Clearpay account once Amazon starts processing
                        your order. This may take a few minutes.
                    </p>
                    <h4>How do I cancel an order?</h4>
                    <p>
                        In the Amazon app, go to your Orders tab, find the relevant order, and
                        select cancel. Once the cancellation is processed, your Clearpay payment
                        plan will be automatically canceled. You'll be refunded for any payments
                        already made, and no further payments will be charged. You can read more
                        about how refunds work{" "}
                        <a
                            href="https://www.clearpay.co.uk/en-GB/help/900005336103-How-do-refunds-work-at-Clearpay"
                            style={{ textDecoration: "underline" }}
                        >
                            here
                        </a>
                    </p>
                </div>
                {showDisclaimer && (
                    <p className={styles.textDetailDisclaimer}>
                        {t("retailerPage.textDetail.section1.disclaimer")}
                    </p>
                )}
            </div>
        );
    };
    return <TextDetail />;
};
